"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import {
  TUTORIAL_MISSIONS,
  TUTORIAL_PAGE_VISIT_MISSIONS,
  getTutorialMission,
  type TutorialMission,
  type TutorialMissionKey,
} from "@/constants/tutorialMissions";
import {
  TUTORIAL_ACTION_EVENT,
  drainTutorialActions,
  reportTutorialAction,
} from "@/lib/tutorial/actions";
import {
  ACTIVE_MISSION_EVENT,
  getBrowserStorage,
  readActiveMission,
  writeActiveMission,
  type ActiveMission,
} from "@/lib/tutorial/activeMission";
import {
  applyClear,
  buildTutorialProgress,
  clearedKeys,
  type TutorialMissionStatusDto,
} from "@/lib/tutorial/progress";
import { matchPageVisit } from "@/lib/tutorial/steps";
import { isOtherOverlayOpen, isStandaloneDisplay } from "@/lib/tutorial/dom";
import {
  TUTORIAL_MISSIONS_QUERY_KEY,
  postTutorialMissionClear,
  useTutorialMissionStatus,
} from "./useTutorialMissions";
import MissionCompanion from "./MissionCompanion";
import TutorialClearModal, { type TutorialClearItem } from "./TutorialClearModal";
import { startTutorialMission } from "./startTutorialMission";

/** 同時にクリアしたもの (登録の 3 つなど) を 1 回のお祝いにまとめるための待ち時間 */
const CELEBRATION_BATCH_MS = 400;
/** ほかのモーダルが閉じるのを待つ間隔 */
const OVERLAY_POLL_MS = 400;

interface Props {
  /** 画面の下の手順の案内を出すか (使い方ガイドの画面では本文の邪魔になるので出さない) */
  showCompanion?: boolean;
}

/**
 * チュートリアルミッションの司令塔 (アプリの全画面と使い方ガイドに 1 つ置く)。
 * - 画面の操作が成功した知らせ (reportTutorialAction) を受けて、未クリアならクリアを頼む
 * - アカウント登録のミッションは、クリア状況を読んだときにクリアを頼む (サーバーが登録状況を確かめる)
 * - 画面を開くだけのミッション (マイページ等) と、ホーム画面に追加したアイコンからの起動は、ここで見つける
 * - クリアしたら「ミッションクリア！」を出す (ほかのモーダルが開いていれば閉じるまで待つ)
 * - 「やってみる」で始めたミッションの案内 (MissionCompanion) を出す
 * 未ログイン (クリア状況が 401) のときは何もしない。
 */
export default function TutorialMissionHost({ showCompanion = true }: Props) {
  const pathname = usePathname() ?? "/";
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: statuses } = useTutorialMissionStatus();

  const [active, setActive] = useState<ActiveMission | null>(null);
  const [waiting, setWaiting] = useState<TutorialClearItem[]>([]);
  const [showing, setShowing] = useState<TutorialClearItem[]>([]);

  const inflightRef = useRef(new Set<TutorialMissionKey>());
  const autoTriedRef = useRef(new Set<TutorialMissionKey>());
  const pendingRef = useRef<TutorialMissionKey[]>([]);

  const cleared = useMemo(() => (statuses ? clearedKeys(statuses) : null), [statuses]);

  // 「やってみる」で始めたミッション (sessionStorage。画面を移っても続く)
  useEffect(() => {
    const sync = () => setActive(readActiveMission(getBrowserStorage("session")));
    sync();
    window.addEventListener(ACTIVE_MISSION_EVENT, sync);
    return () => window.removeEventListener(ACTIVE_MISSION_EVENT, sync);
  }, []);

  const requestClear = useCallback(
    async (key: TutorialMissionKey) => {
      if (!cleared || cleared.has(key) || inflightRef.current.has(key)) return;
      inflightRef.current.add(key);
      try {
        const result = await postTutorialMissionClear(key);
        if (!result) return;
        queryClient.setQueryData<TutorialMissionStatusDto[] | null>(
          TUTORIAL_MISSIONS_QUERY_KEY,
          (prev) => (prev ? applyClear(prev, result) : prev),
        );
        if (result.cleared) {
          const mission = getTutorialMission(result.key);
          setWaiting((prev) => [
            ...prev,
            { key: result.key, title: mission?.title ?? result.key, rewardPoint: result.rewardPoint },
          ]);
          // ヘッダーのポイント・今日の獲得を新しい残高で描き直す
          void queryClient.invalidateQueries({ queryKey: ["userData"] });
          void queryClient.invalidateQueries({ queryKey: ["points"] });
        }
        const session = getBrowserStorage("session");
        if (readActiveMission(session)?.key === key) writeActiveMission(session, null);
      } catch (error: unknown) {
        // 通信の失敗は次の操作でまた頼めるので、画面には出さない
        console.warn("[TutorialMissionHost] clear failed:", error);
      } finally {
        inflightRef.current.delete(key);
      }
    },
    [cleared, queryClient],
  );

  // 溜まっている知らせを処理する。クリア状況の読み込み中は待ち、未ログインなら捨てる
  const flush = useCallback(() => {
    if (statuses === undefined) return;
    const keys = pendingRef.current.splice(0, pendingRef.current.length);
    if (statuses === null) return;
    for (const key of new Set(keys)) void requestClear(key);
  }, [statuses, requestClear]);

  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
    flush();
  }, [flush]);

  useEffect(() => {
    const onAction = () => {
      pendingRef.current.push(...drainTutorialActions());
      flushRef.current();
    };
    onAction(); // この Host ができる前に届いていた知らせ
    window.addEventListener(TUTORIAL_ACTION_EVENT, onAction);
    return () => window.removeEventListener(TUTORIAL_ACTION_EVENT, onAction);
  }, []);

  // アカウント登録のミッション: クリア状況を読んだら 1 回だけクリアを頼む
  useEffect(() => {
    if (!cleared) return;
    for (const mission of TUTORIAL_MISSIONS) {
      if (mission.clear !== "auto" || cleared.has(mission.key)) continue;
      if (autoTriedRef.current.has(mission.key)) continue;
      autoTriedRef.current.add(mission.key);
      void requestClear(mission.key);
    }
  }, [cleared, requestClear]);

  // 画面を開くだけでクリアになるミッション
  useEffect(() => {
    const key = matchPageVisit(TUTORIAL_PAGE_VISIT_MISSIONS, pathname);
    if (key) reportTutorialAction(key);
  }, [pathname]);

  // ホーム画面に追加したアイコンから開いた
  useEffect(() => {
    if (isStandaloneDisplay()) reportTutorialAction("home_screen");
  }, []);

  // お祝い: 少し待ってまとめ、ほかのモーダル・ツアーが閉じてから出す
  useEffect(() => {
    if (waiting.length === 0 || showing.length > 0) return;
    let timer: ReturnType<typeof setTimeout>;
    const tryShow = () => {
      if (isOtherOverlayOpen()) {
        timer = setTimeout(tryShow, OVERLAY_POLL_MS);
        return;
      }
      setShowing(waiting);
      setWaiting([]);
    };
    timer = setTimeout(tryShow, CELEBRATION_BATCH_MS);
    return () => clearTimeout(timer);
  }, [waiting, showing]);

  const progress = useMemo(
    () => (statuses ? buildTutorialProgress(statuses) : null),
    [statuses],
  );

  if (statuses === null || statuses === undefined) return null;

  const activeMission = active ? getTutorialMission(active.key) : undefined;
  const companionMission =
    showCompanion &&
    activeMission &&
    activeMission.steps.length > 0 &&
    cleared &&
    !cleared.has(activeMission.key)
      ? activeMission
      : null;

  const closeCelebration = () => setShowing([]);
  const startNext = (mission: TutorialMission) => {
    setShowing([]);
    startTutorialMission(mission, { pathname, push: (href) => router.push(href) });
  };

  return (
    <>
      {companionMission && active && (
        <MissionCompanion
          key={companionMission.key}
          mission={companionMission}
          manualIndex={active.manualIndex}
          onManualIndexChange={(index) =>
            writeActiveMission(getBrowserStorage("session"), {
              key: companionMission.key,
              manualIndex: index,
            })
          }
          onClose={() => writeActiveMission(getBrowserStorage("session"), null)}
        />
      )}
      <TutorialClearModal
        items={showing}
        clearedCount={progress?.clearedCount ?? 0}
        total={progress?.total ?? TUTORIAL_MISSIONS.length}
        next={progress?.next ?? null}
        onNext={startNext}
        onClose={closeCelebration}
      />
    </>
  );
}
